import React from "react";
import { AlertTriangle, ArrowRight } from "lucide-react";

/**
 * StateConfirmModal - Confirmation modal shown when the user selects a property 
 * in a jurisdiction (city/state) that doesn't match their current target selection.
 */
const StateConfirmModal = ({
    isOpen,
    selectedAddress,
    targetCity,
    targetState,
    currentCity,
    currentState,
    onConfirm,
    onCancel
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden border border-slate-100 animate-slideUp">
                {/* Header */}
                <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                            <AlertTriangle className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h3 className="text-white font-bold text-lg leading-tight">Switch Target Jurisdiction?</h3>
                            <p className="text-white/80 text-xs mt-0.5">Selected address is in a different location</p>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="p-6">
                    <p className="text-slate-600 text-sm mb-5 leading-relaxed">
                        You selected an address in <span className="font-bold text-slate-800">{selectedAddress}</span>, but your active target is currently set to California rules. 
                    </p>

                    {/* Visual Comparison Flow */}
                    <div className="flex items-center justify-between gap-3 p-4 bg-slate-50 border border-slate-100 rounded-xl mb-5">
                        <div className="flex flex-col min-w-0">
                            <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Current Target</span>
                            <span className="text-sm font-bold text-slate-700 truncate mt-1">
                                {currentCity}, {currentState}
                            </span>
                        </div>
                        
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-200/60 flex items-center justify-center">
                            <ArrowRight className="w-4 h-4 text-slate-400" />
                        </div>

                        <div className="flex flex-col min-w-0 text-right">
                            <span className="text-[10px] font-black uppercase text-amber-600 tracking-wider">New Target</span>
                            <span className="text-sm font-bold text-amber-700 truncate mt-1">
                                {targetCity}, {targetState}
                            </span>
                        </div>
                    </div>

                    <p className="text-slate-500 text-xs leading-normal">
                        Switching will update the active zoning rules, county service connection, and neighbor search bounds to match the new location.
                    </p>
                </div>

                {/* Footer Buttons */}
                <div className="px-6 pb-6 flex gap-3">
                    <button
                        onClick={onCancel}
                        className="flex-1 py-3 px-4 rounded-xl border-2 border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50 transition-all focus:outline-none"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold text-sm hover:from-amber-600 hover:to-orange-700 transition-all shadow-lg shadow-amber-200 focus:outline-none"
                    >
                        Yes, Switch Target
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StateConfirmModal;
